/**
 * Device Operations Page - Frontend
 * @description Página principal de operaciones - muestra dispositivos del usuario con tabla moderna
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

// Components
import { DeviceDashboardTable } from '@/components/features/operations';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';

// Services and mappers
import { dashboardService } from '@/services/api';

// Hooks and contexts
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import { useExcelExport } from '@/hooks/useExcelExport';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';
import { formatTimezoneShort } from '@/utils/formatters/timezoneFormatter';
import { formatVehicleTypeForDisplay } from '@/config/deviceTypes.config';
import i18n from '@/i18n/i18n';

// Types
import { DeviceDashboardTableData } from '@/types/operational-ui.types';
import { DeviceStatus } from '@/types/device.types';

const sortDevicesByLastOperationDesc = (
  devices: DeviceDashboardTableData[]
): DeviceDashboardTableData[] =>
  [...devices].sort((a, b) => {
    const timeA = a.lastOperation ? new Date(a.lastOperation).getTime() : 0;
    const timeB = b.lastOperation ? new Date(b.lastOperation).getTime() : 0;
    return timeB - timeA;
  });

export const AllDeviceByUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const { exportDevices, generateFileName, isExporting } = useExcelExport({ showToasts: false });

  const [devices, setDevices] = useState<DeviceDashboardTableData[]>([]);
  const [loading, setLoading] = useState(true);
  // refreshing state removed since it's now handled by the table component

  const loadDevicesData = useCallback(
    async () => {
      if (!user?.id) return;

      try {
        setLoading(true);

        // Use the new optimized dashboard service
        const dashboardDeviceResponse = await dashboardService.getDevicesByUserId(user.id);
        if (!dashboardDeviceResponse.success) {
          throw new Error(dashboardDeviceResponse.error?.message || 'Failed to get user devices');
        }

        // Convert dashboard devices to the format expected by the existing table
        const tableData: DeviceDashboardTableData[] = dashboardDeviceResponse.data.map(
          (dashboardDevice) => ({
            id: dashboardDevice.id,
            serialNumber: dashboardDevice.serialNumber,
            plate: dashboardDevice.plate || '',
            alias: dashboardDevice.alias || '',
            type: dashboardDevice.type || '',
            status: dashboardDevice.status as DeviceStatus,
            vehicle: dashboardDevice.plate || dashboardDevice.serialNumber,
            lastConnection: dashboardDevice.lastConnection,
            lastOperation: dashboardDevice.lastOperation,
            operationCount: dashboardDevice.operationCount,
            lastConnectionTz: dashboardDevice.lastConnectionTz,
            lastOperationTz: dashboardDevice.lastOperationTz,
            lastOperationFormatted: dashboardDevice.lastOperation
              ? new Date(dashboardDevice.lastOperation).toLocaleDateString()
              : 'N/A',
            deviceId: dashboardDevice.id,
            lastActivity: dashboardDevice.lastOperation
              ? new Date(dashboardDevice.lastOperation).toLocaleDateString()
              : 'N/A',
          })
        );

        setDevices(sortDevicesByLastOperationDesc(tableData));
      } catch (error) {
        console.error('Error loading devices:', error);
        showToast(t('pages.operations.devices.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id] // Remove showToast and t from dependencies to prevent infinite loops
  );

  useEffect(() => {
    if (user?.id) {
      loadDevicesData();
    }
  }, [user?.id, loadDevicesData]);

  const handleViewOperations = async (deviceId: string) => {
    await navigateWithTokenCheck(navigate, `/operations/device/${deviceId}`);
  };

  const handleViewAllLectures = async (deviceId: string) => {
    await navigateWithTokenCheck(navigate, `/operations/device/${deviceId}/all-lectures`);
  };

  const handleViewAllPendingLectures = async (deviceId: string) => {
    await navigateWithTokenCheck(navigate, `/operations/device/${deviceId}/pending-lectures`);
  };

  const handleRefresh = () => {
    loadDevicesData();
  };

  // Función para exportar a CSV
  const handleExportCSV = useCallback(() => {
    if (devices.length === 0) {
      showToast(t('common.noDataToExport'), 'warning');
      return;
    }

    // Definir las columnas que queremos exportar (traducidas)
    const headers = [
      t('pages.operations.devices.columns.serialNumber'),
      t('pages.operations.devices.columns.plate'),
      t('pages.operations.devices.columns.alias'),
      t('pages.operations.devices.columns.type'),
      t('pages.operations.devices.columns.status'),
      t('pages.operations.devices.columns.lastConnection'),
      t('pages.operations.devices.columns.lastConnectionZone'),
      t('pages.operations.devices.columns.lastOperation'),
      t('pages.operations.devices.columns.lastOperationZone'),
      t('pages.operations.devices.columns.operationCount'),
    ];

    // Función para escapar valores CSV
    const escapeCSV = (value: string | number) => {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Crear el contenido CSV
    const csvRows = [
      headers.join(','),
      ...devices.map((device) => {
        const connTz = device.lastConnectionTz ?? 'UTC';
        const opTz = device.lastOperationTz ?? 'UTC';
        return [
          device.serialNumber || '',
          device.plate || '',
          device.alias || '',
          formatVehicleTypeForDisplay(
            device.type,
            i18n.language === 'es' ? 'es' : 'en'
          ),
          t(`pages.operations.devices.status.${device.status.toLowerCase()}`),
          device.lastConnection
            ? formatDateAsYearMonthDayHourMinute(device.lastConnection, connTz)
            : 'N/A',
          formatTimezoneShort(connTz),
          device.lastOperation
            ? formatDateAsYearMonthDayHourMinute(device.lastOperation, opTz)
            : 'N/A',
          formatTimezoneShort(opTz),
          device.operationCount || 0,
        ]
          .map(escapeCSV)
          .join(',');
      }),
    ];

    const csvContent = csvRows.join('\n');

    // Crear y descargar el archivo
    const blob = new globalThis.Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = globalThis.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `dispositivos_${user?.id || 'user'}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    globalThis.URL.revokeObjectURL(url);

    showToast(t('common.exportSuccessful'), 'success');
  }, [devices, user?.id, showToast, t]);

  // Función para exportar a XLSX
  const handleExportXLSX = useCallback(async () => {
    try {
      const fileName = generateFileName('dispositivos', user?.id || 'user');
      await exportDevices(devices, fileName, user ? { id: user.id } : undefined);
      showToast(t('common.exportSuccessful'), 'success');
    } catch (error) {
      showToast(
        `❌ Error al exportar: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        'error'
      );
    }
  }, [devices, user, exportDevices, generateFileName, showToast, t]);

  const handleExport = (format: 'csv' | 'excel') => {
    if (format === 'csv') {
      handleExportCSV();
    } else {
      handleExportXLSX();
    }
  };

  const handleCenterMap = (coordinates: { lat: number; lng: number }) => {
    console.log('Center map on coordinates:', coordinates);
    // Future MapBox integration
  };

  return (
    <div className="space-y-6">
      {/* Loading Overlay - Reusable Component */}
      <LoadingOverlay
        loading={loading}
        title={t('common.loading')}
        message={t('pages.operations.devices.loadingDevices')}
      />

      {/* Header with Export Buttons */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('pages.operations.devices.title')}
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">
                {t('pages.operations.devices.totalDevices')}:
              </span>
              <span className="ml-2 text-gray-900">{devices.length}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                {t('pages.operations.devices.totalOperations')}:
              </span>
              <span className="ml-2 text-gray-900">
                {devices.reduce((sum, device) => sum + (device.operationCount || 0), 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh')}
          </button>

          <button
            onClick={handleExportCSV}
            disabled={devices.length === 0}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </button>

          <button
            onClick={handleExportXLSX}
            disabled={devices.length === 0 || isExporting}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exportando...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar XLSX
              </>
            )}
          </button>
        </div>
      </div>

      {/* Device Dashboard Table */}
      <DeviceDashboardTable
        devices={devices}
        loading={false}
        onViewOperations={handleViewOperations}
        onViewAllLectures={handleViewAllLectures}
        onViewAllPendingLectures={handleViewAllPendingLectures}
        onRefresh={undefined}
        onExport={handleExport}
        showAdvancedExport={false}
        actions={{
          onCenterMap: handleCenterMap,
        }}
      />
    </div>
  );
};
